// ReceiptInfoPage.jsx (FULL - MongoDB backend uyumlu)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = (
  (typeof process !== "undefined" && process.env?.REACT_APP_MERCHANT_API_BASE_URL) ||
  "https://kdspanel-production.up.railway.app/api/merchant"
).replace(/\/+$/, "");

const getAuthToken = () => localStorage.getItem("userToken");
const getAuthCard = () => localStorage.getItem("card");

const redirectToLogin = () => {
  localStorage.removeItem("userToken");
  localStorage.removeItem("card");
  window.location.href = "/mlogin";
};

const normalizeDepartmentsFromBackend = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? "").trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    const cleaned = value.trim();
    return cleaned ? [cleaned] : [];
  }

  return [];
};

const normalizeDepartmentVatText = (value) => {
  const raw = String(value ?? "")
    .trim()
    .replace(/,/g, ".")
    .replace(/\s+/g, "");

  if (!raw) return "";

  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return "";

  return Number.isInteger(n) ? String(n) : String(n);
};

const buildDepartmentText = (name, number, vatRate) => {
  const cleanName = String(name ?? "")
    .trim()
    .replace(/,/g, " ")
    .replace(/\s+/g, " ");

  const cleanNumber = String(number ?? "")
    .trim()
    .replace(/,/g, "")
    .replace(/\s+/g, "");

  const cleanVatRate = normalizeDepartmentVatText(vatRate);

  if (!cleanName || !cleanNumber || !cleanVatRate) return "";
  return `${cleanName},${cleanNumber},${cleanVatRate}`;
};

const parseDepartmentText = (value) => {
  const raw = String(value ?? "").trim();
  const [namePart = "", numberPart = "", vatPart = ""] = raw.split(",");

  return {
    name: namePart.trim(),
    number: numberPart.trim(),
    vatRate: vatPart.trim(),
  };
};

const normalizeDepartmentNumberForCompare = (value) => {
  const cleaned = String(value ?? "")
    .trim()
    .replace(/,/g, "")
    .replace(/\s+/g, "");

  if (!cleaned) return "";

  const numeric = Number(cleaned);
  if (Number.isFinite(numeric)) return String(numeric);

  return cleaned.toLowerCase();
};

const hasDuplicateDepartmentNumber = (departments, targetNumber, ignoreIndex = null) => {
  const normalizedTarget = normalizeDepartmentNumberForCompare(targetNumber);
  if (!normalizedTarget) return false;

  return normalizeDepartmentsFromBackend(departments).some((department, index) => {
    if (ignoreIndex !== null && index === ignoreIndex) return false;
    const parsed = parseDepartmentText(department);
    return normalizeDepartmentNumberForCompare(parsed.number) === normalizedTarget;
  });
};

const INITIAL_FORM = {
  // Genel
  companyName: "",

  // Şube Adres
  branchName: "",
  branchStreet: "",
  branchNeighborhood: "",
  branchWorkplaceNo: "",
  branchTaxOffice: "",
  branchTaxNo: "",
  branchMersisNo: "",
  branchCity: "",
  branchDistrict: "",

  // Merkez Bilgiler
  centerStreet: "",
  centerNeighborhood: "",
  centerWorkplaceNo: "",
  centerTaxOffice: "",
  centerTaxNo: "",
  centerMersisNo: "",
  centerCity: "",
  centerDistrict: "",
  centerWebsite: "", // zorunlu değil
  centerChamber: "",
  centerChamberNo: "",
  centerPhone: "",

  // Departmanlar: MongoDB içinde tek alan olarak tutulur.
  // Örnek: departments: ["su,1,20", "yiyecek,2,10"]
  departments: [],
};

const ReceiptInfoPage = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState(INITIAL_FORM);

  const [attemptedSave, setAttemptedSave] = useState(false);
  const [saving, setSaving] = useState(false);

  // ✅ İlk yükleme tamamlanana kadar sayfayı hiç gösterme
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // ✅ iscompleted bilgisi (backend gönderirse)
  const [isCompleted, setIsCompleted] = useState(false);

  // ✅ Kaydedilmiş son snapshot (dirty kontrolü)
  const savedSnapshotRef = useRef(JSON.stringify(INITIAL_FORM));
  const [isDirty, setIsDirty] = useState(false);

  // ✅ "Kaydetmeden gitmek istiyor musunuz?" modal state
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const pendingNavRef = useRef(null); // string path veya number (-1)

  // ✅ Departman yönetimi
  const [departmentEditorOpen, setDepartmentEditorOpen] = useState(false);
  const [departmentName, setDepartmentName] = useState("");
  const [departmentNumber, setDepartmentNumber] = useState("");
  const [departmentVatRate, setDepartmentVatRate] = useState("");
  const [editingDepartmentIndex, setEditingDepartmentIndex] = useState(null);
  const [departmentSaving, setDepartmentSaving] = useState(false);
  const [departmentError, setDepartmentError] = useState("");

  const requiredFields = useMemo(
    () => [
      { key: "companyName", label: "Şirket İsmi" },

      // Şube
      { key: "branchName", label: "Şube İsmi" },
      { key: "branchStreet", label: "Cadde" },
      { key: "branchNeighborhood", label: "Mahalle" },
      { key: "branchWorkplaceNo", label: "İşyeri No" },
      { key: "branchTaxOffice", label: "Vergi Dairesi" },
      { key: "branchTaxNo", label: "Vergi No" },
      { key: "branchMersisNo", label: "Mersis No" },
      { key: "branchCity", label: "Şehir" },
      { key: "branchDistrict", label: "İlçe" },

      // Merkez
      { key: "centerStreet", label: "Cadde" },
      { key: "centerNeighborhood", label: "Mahalle" },
      { key: "centerWorkplaceNo", label: "İşyeri No" },
      { key: "centerTaxOffice", label: "Vergi Dairesi" },
      { key: "centerTaxNo", label: "Vergi No" },
      { key: "centerMersisNo", label: "Mersis No" },
      { key: "centerCity", label: "Şehir" },
      { key: "centerDistrict", label: "İlçe" },
      // centerWebsite zorunlu değil
      { key: "centerChamber", label: "Ticaret Odası" },
      { key: "centerChamberNo", label: "Ticaret Odası No" },
      { key: "centerPhone", label: "Tel No" },
    ],
    []
  );

  const errors = useMemo(() => {
    const e = {};
    for (const f of requiredFields) {
      const val = (form[f.key] ?? "").toString().trim();
      if (!val) e[f.key] = `${f.label} zorunludur.`;
    }
    return e;
  }, [form, requiredFields]);

  const isValid = Object.keys(errors).length === 0;
  const missingCount = Object.keys(errors).length;

  // ✅ Dirty kontrolü
  useEffect(() => {
    const current = JSON.stringify(form);
    setIsDirty(current !== savedSnapshotRef.current);
  }, [form]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // =========================
  // ✅ BACKEND MAPPING HELPERS
  // =========================
  const toBackendPayload = (f) => {
    const card = getAuthCard();
    return {
      card,

      // Genel
      genelsirketisim: f.companyName,

      // Şube
      sbsubeismi: f.branchName,
      sbcadde: f.branchStreet,
      sbmahalle: f.branchNeighborhood,
      sbisyerino: f.branchWorkplaceNo,
      sbvergidairesi: f.branchTaxOffice,
      sbvergino: f.branchTaxNo,
      sbmersisno: f.branchMersisNo,
      sbsehir: f.branchCity,
      sbilce: f.branchDistrict,

      // Merkez
      merkezcadde: f.centerStreet,
      merkezmahalle: f.centerNeighborhood,
      merkezisyerino: f.centerWorkplaceNo,
      merkezvergidairesi: f.centerTaxOffice,
      merkezvergino: f.centerTaxNo,
      merkezmersisno: f.centerMersisNo,
      merkezsehir: f.centerCity,
      merkezilce: f.centerDistrict,
      merkezwebsite: f.centerWebsite, // zorunlu değil
      merkezticaretodasi: f.centerChamber,
      merkezticaretodasino: f.centerChamberNo,
      merkeztelno: f.centerPhone,

      // Departmanlar MongoDB'de tek field olarak array<string> tutulur.
      // Her kayıt tek string: "departman ismi,departman numarası"
      departments: normalizeDepartmentsFromBackend(f.departments),
    };
  };

  const fromBackendToForm = (data) => {
    const d = data || {};
    return {
      companyName: d.genelsirketisim || "",

      branchName: d.sbsubeismi || "",
      branchStreet: d.sbcadde || "",
      branchNeighborhood: d.sbmahalle || "",
      branchWorkplaceNo: d.sbisyerino || "",
      branchTaxOffice: d.sbvergidairesi || "",
      branchTaxNo: d.sbvergino || "",
      branchMersisNo: d.sbmersisno || "",
      branchCity: d.sbsehir || "",
      branchDistrict: d.sbilce || "",

      centerStreet: d.merkezcadde || "",
      centerNeighborhood: d.merkezmahalle || "",
      centerWorkplaceNo: d.merkezisyerino || "",
      centerTaxOffice: d.merkezvergidairesi || "",
      centerTaxNo: d.merkezvergino || "",
      centerMersisNo: d.merkezmersisno || "",
      centerCity: d.merkezsehir || "",
      centerDistrict: d.merkezilce || "",
      centerWebsite: d.merkezwebsite || "",
      centerChamber: d.merkezticaretodasi || "",
      // ✅ typo fix: sadece bu alan düzeltildi
      centerChamberNo: d.merkezticaretodasino || "",
      centerPhone: d.merkeztelno || "",
      departments: normalizeDepartmentsFromBackend(d.departments),
    };
  };

  const apiPost = async (path, bodyObj, { signal } = {}) => {
    const token = getAuthToken();
    const card = getAuthCard();

    if (!token || !card) {
      redirectToLogin();
      return null;
    }

    const res = await fetch(`${API_BASE_URL}/${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(bodyObj),
      signal,
    });

    let json = null;
    try {
      json = await res.json();
    } catch {
      // ignore
    }

    if (!res.ok) {
      if (json?.redirectToLogin) redirectToLogin();
      const msg = json?.message || json?.error || "İstek başarısız";
      throw new Error(msg);
    }

    return json;
  };

  // ✅ İlk açılışta backend’den doldur (bitene kadar loader göster)
  useEffect(() => {
    const controller = new AbortController();

    const run = async () => {
      try {
        setLoading(true);
        setLoadError("");

        const card = getAuthCard();
        const data = await apiPost("getfisbilgi", { card }, { signal: controller.signal });

        // ✅ iscompleted gelirse al
        setIsCompleted(!!data?.data?.iscompleted);

        const filled = fromBackendToForm(data?.data);
        setForm(filled);

        // ✅ Bu hali “kaydedilmiş” kabul edelim (dirty olmasın)
        savedSnapshotRef.current = JSON.stringify(filled);
        setIsDirty(false);
      } catch (e) {
        if (e?.name === "AbortError") return;
        console.error("getfisbilgi error:", e);

        setLoadError(e?.message || "Fiş bilgileri yüklenemedi.");
        // Form default haliyle kalır
        savedSnapshotRef.current = JSON.stringify(INITIAL_FORM);
        setIsDirty(false);
      } finally {
        setLoading(false);
      }
    };

    run();
    return () => controller.abort();
  }, []);

  // ✅ Backend’e kayıt
  const saveToBackend = async (currentForm) => {
    const payload = toBackendPayload(currentForm);
    const r = await apiPost("savefisbilgi", payload);
    // savefisbilgi response'unda result.iscompleted dönüyorsa yakala (senin backend öyle dönüyor)
    const newCompleted = !!(r?.result?.iscompleted);
    setIsCompleted(newCompleted);
    return r;
  };

  // ✅ Kaydet
  const handleSave = async ({ showAlert = true } = {}) => {
    setAttemptedSave(true);

    if (!isValid) {
      return false;
    }

    try {
      setSaving(true);
      await saveToBackend(form);

      // ✅ Kaydedildi snapshot
      savedSnapshotRef.current = JSON.stringify(form);
      setIsDirty(false);

      if (showAlert) alert("Kaydedildi ✅");
      return true;
    } catch (err) {
      console.error("Save error:", err);
      if (showAlert)
        alert(err?.message ? `Kaydetme hatası ❌\n${err.message}` : "Kaydetme sırasında hata oluştu ❌");
      return false;
    } finally {
      setSaving(false);
    }
  };

  // ✅ Navigasyon guard
  const requestNavigate = (target) => {
    if (isDirty) {
      pendingNavRef.current = target;
      setLeaveModalOpen(true);
      return;
    }
    doNavigate(target);
  };

  const doNavigate = (target) => {
    if (typeof target === "number") navigate(target);
    else navigate(target);
  };

  const onLeaveWithoutSaving = () => {
    const target = pendingNavRef.current;
    pendingNavRef.current = null;
    setLeaveModalOpen(false);
    doNavigate(target);
  };

  const onCancelLeave = () => {
    pendingNavRef.current = null;
    setLeaveModalOpen(false);
  };

  const onSaveAndContinue = async () => {
    const ok = await handleSave({ showAlert: false });
    if (!ok) return;

    const target = pendingNavRef.current;
    pendingNavRef.current = null;
    setLeaveModalOpen(false);
    doNavigate(target);
  };


  const resetDepartmentEditor = () => {
    setDepartmentEditorOpen(false);
    setDepartmentName("");
    setDepartmentNumber("");
    setDepartmentVatRate("");
    setEditingDepartmentIndex(null);
    setDepartmentError("");
  };

  const openNewDepartmentEditor = () => {
    setDepartmentEditorOpen(true);
    setDepartmentName("");
    setDepartmentNumber("");
    setDepartmentVatRate("");
    setEditingDepartmentIndex(null);
    setDepartmentError("");
  };

  const openEditDepartmentEditor = (index) => {
    const departments = normalizeDepartmentsFromBackend(form.departments);
    const parsed = parseDepartmentText(departments[index]);

    setDepartmentEditorOpen(true);
    setDepartmentName(parsed.name);
    setDepartmentNumber(parsed.number);
    setDepartmentVatRate(parsed.vatRate);
    setEditingDepartmentIndex(index);
    setDepartmentError("");
  };

  const persistDepartments = async (nextDepartments) => {
    const nextForm = {
      ...form,
      departments: normalizeDepartmentsFromBackend(nextDepartments),
    };

    setDepartmentSaving(true);
    setDepartmentError("");

    try {
      await saveToBackend(nextForm);
      setForm(nextForm);
      savedSnapshotRef.current = JSON.stringify(nextForm);
      setIsDirty(false);
      return true;
    } catch (err) {
      console.error("Department save error:", err);
      setDepartmentError(err?.message || "Departman kaydedilemedi.");
      return false;
    } finally {
      setDepartmentSaving(false);
    }
  };

  const handleSaveDepartment = async () => {
    const departmentText = buildDepartmentText(departmentName, departmentNumber, departmentVatRate);

    if (!departmentText) {
      setDepartmentError("Departman ismi, departman numarası ve KDV oranı zorunludur.");
      return;
    }

    const currentDepartments = normalizeDepartmentsFromBackend(form.departments);
    const ignoreIndex =
      editingDepartmentIndex !== null && editingDepartmentIndex >= 0
        ? editingDepartmentIndex
        : null;

    if (hasDuplicateDepartmentNumber(currentDepartments, departmentNumber, ignoreIndex)) {
      setDepartmentError(`Bu departman numarası zaten kayıtlı: ${String(departmentNumber).trim()}`);
      return;
    }

    const nextDepartments = [...currentDepartments];

    if (editingDepartmentIndex !== null && editingDepartmentIndex >= 0) {
      nextDepartments[editingDepartmentIndex] = departmentText;
    } else {
      nextDepartments.push(departmentText);
    }

    const ok = await persistDepartments(nextDepartments);
    if (ok) resetDepartmentEditor();
  };

  const handleDeleteDepartment = async (index) => {
    const currentDepartments = normalizeDepartmentsFromBackend(form.departments);
    const target = currentDepartments[index] || "";

    if (!target) return;

    const yes = window.confirm("Bu departmanı silmek istiyor musunuz?");
    if (!yes) return;

    const nextDepartments = currentDepartments.filter((_, i) => i !== index);
    await persistDepartments(nextDepartments);

    if (editingDepartmentIndex === index) {
      resetDepartmentEditor();
    }
  };

  // ✅ Bilgiler gelene kadar full-screen loader
  if (loading) {
    return <FullPageLoader title="Fiş bilgileri yükleniyor..." subtitle="Lütfen bekleyin." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-6xl px-4 py-8">
        {/* Yükleme hatası banner */}
        {loadError && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span className="font-semibold">Uyarı:</span> {loadError} (Yine de formu doldurup kaydedebilirsin.)
          </div>
        )}

        {/* ÜST BAR */}
        <div className="flex items-start justify-between gap-4">
          {/* SOL: Route Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => requestNavigate("/")}
              className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50"
            >
              Ürün Paneli
            </button>

            <button
              onClick={() => requestNavigate("/designpanel")}
              className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50"
            >
              Dizayn Paneli
            </button>

            <button
              onClick={() => requestNavigate("/preferance")}
              className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50"
            >
              Tercihler
            </button>

            <button
              onClick={() => requestNavigate("/excelfis")}
              className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-bold text-amber-800 shadow-sm hover:bg-amber-100"
            >
              Excel Fiş
            </button>

            <button
              onClick={() => requestNavigate("/branches")}
              className="rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-sm font-bold text-white shadow-sm hover:bg-slate-900"
            >
              Şubeler
            </button>

            <button
              onClick={() => requestNavigate("/appointments")}
              className="rounded-xl border border-fuchsia-700 bg-fuchsia-600 px-3 py-2 text-sm font-bold text-white shadow-sm hover:bg-fuchsia-700"
            >
              Randevular
            </button>
          </div>

          {/* SAĞ: Başlık + Kaydet */}
          <div className="flex items-start gap-3">
            <div className="hidden sm:block text-right">
              <h1 className="text-2xl font-semibold text-gray-900">Fiş Bilgileri</h1>
              <p className="mt-1 text-sm text-gray-600">Tüm zorunlu alanları doldurmadan kaydetme işlemi yapılamaz.</p>
            </div>

            <button
              onClick={() => handleSave({ showAlert: true })}
              disabled={saving}
              className="shrink-0 rounded-xl bg-black px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </div>

        {/* Mobilde başlığı göster */}
        <div className="mt-4 sm:hidden">
          <h1 className="text-2xl font-semibold text-gray-900">Fiş Bilgileri</h1>
          <p className="mt-1 text-sm text-gray-600">Tüm zorunlu alanları doldurmadan kaydetme işlemi yapılamaz.</p>
        </div>

        {/* Global error banner */}
        {attemptedSave && !isValid && (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span className="font-semibold">Tüm bilgileri doldurmanız gereklidir.</span> Eksik alanlar kırmızı ile
            işaretlendi.
          </div>
        )}

        {/* ANA GRID: Sol Form + Sağ Uyarı */}
        <div className="mt-6 grid grid-cols-12 gap-6">
          {/* SOL: FORM */}
          <div className="col-span-12 lg:col-span-8">
            {/* Genel */}
            <Card title="Genel">
              <TwoColRow
                label="Şirket İsmi"
                required
                value={form.companyName}
                onChange={(v) => setField("companyName", v)}
                placeholder="Örn: Frostyway Company LTD"
                error={attemptedSave ? errors.companyName : ""}
              />
            </Card>

            {/* Şube Adres */}
            <Card title="Şube Adres">
              <TwoColRow
                label="Şube İsmi"
                required
                value={form.branchName}
                onChange={(v) => setField("branchName", v)}
                placeholder="Örn: Merkez Şube"
                error={attemptedSave ? errors.branchName : ""}
              />
              <TwoColRow
                label="Cadde"
                required
                value={form.branchStreet}
                onChange={(v) => setField("branchStreet", v)}
                placeholder="Örn: İstiklal Cd."
                error={attemptedSave ? errors.branchStreet : ""}
              />
              <TwoColRow
                label="Mahalle"
                required
                value={form.branchNeighborhood}
                onChange={(v) => setField("branchNeighborhood", v)}
                placeholder="Örn: Beyoğlu"
                error={attemptedSave ? errors.branchNeighborhood : ""}
              />
              <TwoColRow
                label="İşyeri No"
                required
                value={form.branchWorkplaceNo}
                onChange={(v) => setField("branchWorkplaceNo", v)}
                placeholder="Örn: 12A"
                error={attemptedSave ? errors.branchWorkplaceNo : ""}
              />
              <TwoColRow
                label="Vergi Dairesi"
                required
                value={form.branchTaxOffice}
                onChange={(v) => setField("branchTaxOffice", v)}
                placeholder="Örn: Beyoğlu VD"
                error={attemptedSave ? errors.branchTaxOffice : ""}
              />
              <TwoColRow
                label="Vergi No"
                required
                value={form.branchTaxNo}
                onChange={(v) => setField("branchTaxNo", v)}
                placeholder="Örn: 1234567890"
                error={attemptedSave ? errors.branchTaxNo : ""}
              />
              <TwoColRow
                label="Mersis No"
                required
                value={form.branchMersisNo}
                onChange={(v) => setField("branchMersisNo", v)}
                placeholder="Örn: 0123456789012345"
                error={attemptedSave ? errors.branchMersisNo : ""}
              />
              <TwoColRow
                label="Şehir"
                required
                value={form.branchCity}
                onChange={(v) => setField("branchCity", v)}
                placeholder="Örn: İstanbul"
                error={attemptedSave ? errors.branchCity : ""}
              />
              <TwoColRow
                label="İlçe"
                required
                value={form.branchDistrict}
                onChange={(v) => setField("branchDistrict", v)}
                placeholder="Örn: Beyoğlu"
                error={attemptedSave ? errors.branchDistrict : ""}
              />
            </Card>

            {/* Merkez Bilgiler */}
            <Card title="Merkez Bilgiler">
              <TwoColRow
                label="Cadde"
                required
                value={form.centerStreet}
                onChange={(v) => setField("centerStreet", v)}
                placeholder="Örn: Atatürk Cd."
                error={attemptedSave ? errors.centerStreet : ""}
              />
              <TwoColRow
                label="Mahalle"
                required
                value={form.centerNeighborhood}
                onChange={(v) => setField("centerNeighborhood", v)}
                placeholder="Örn: Levent"
                error={attemptedSave ? errors.centerNeighborhood : ""}
              />
              <TwoColRow
                label="İşyeri No"
                required
                value={form.centerWorkplaceNo}
                onChange={(v) => setField("centerWorkplaceNo", v)}
                placeholder="Örn: 5"
                error={attemptedSave ? errors.centerWorkplaceNo : ""}
              />
              <TwoColRow
                label="Vergi Dairesi"
                required
                value={form.centerTaxOffice}
                onChange={(v) => setField("centerTaxOffice", v)}
                placeholder="Örn: Şişli VD"
                error={attemptedSave ? errors.centerTaxOffice : ""}
              />
              <TwoColRow
                label="Vergi No"
                required
                value={form.centerTaxNo}
                onChange={(v) => setField("centerTaxNo", v)}
                placeholder="Örn: 1234567890"
                error={attemptedSave ? errors.centerTaxNo : ""}
              />
              <TwoColRow
                label="Mersis No"
                required
                value={form.centerMersisNo}
                onChange={(v) => setField("centerMersisNo", v)}
                placeholder="Örn: 0123456789012345"
                error={attemptedSave ? errors.centerMersisNo : ""}
              />

              <TwoColRow
                label="Şehir"
                required
                value={form.centerCity}
                onChange={(v) => setField("centerCity", v)}
                placeholder="Örn: İstanbul"
                error={attemptedSave ? errors.centerCity : ""}
              />
              <TwoColRow
                label="İlçe"
                required
                value={form.centerDistrict}
                onChange={(v) => setField("centerDistrict", v)}
                placeholder="Örn: Şişli"
                error={attemptedSave ? errors.centerDistrict : ""}
              />

              <TwoColRow
                label={
                  <span className="inline-flex items-center gap-2">
                    Website <span className="text-xs text-gray-500">(zorunlu değil)</span>
                  </span>
                }
                value={form.centerWebsite}
                onChange={(v) => setField("centerWebsite", v)}
                placeholder="Örn: https://site.com"
                error=""
              />

              <TwoColRow
                label="Ticaret Odası"
                required
                value={form.centerChamber}
                onChange={(v) => setField("centerChamber", v)}
                placeholder="Örn: İstanbul Ticaret Odası"
                error={attemptedSave ? errors.centerChamber : ""}
              />
              <TwoColRow
                label="Ticaret Odası No"
                required
                value={form.centerChamberNo}
                onChange={(v) => setField("centerChamberNo", v)}
                placeholder="Örn: 123456"
                error={attemptedSave ? errors.centerChamberNo : ""}
              />
              <TwoColRow
                label="Tel No"
                required
                value={form.centerPhone}
                onChange={(v) => setField("centerPhone", v)}
                placeholder="Örn: +90 555 000 00 00"
                error={attemptedSave ? errors.centerPhone : ""}
              />
            </Card>

            <div className="mt-6 text-xs text-gray-500">
              Not: Website alanı isteğe bağlıdır; diğer tüm alanlar zorunludur.
            </div>
          </div>

          {/* SAĞ: UYARI */}
          <div className="col-span-12 lg:col-span-4">
            <div className="sticky top-6 space-y-4">
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 9v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <path d="M12 17h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      <path
                        d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  <div>
                    <div className="text-sm font-semibold text-amber-900">Uyarı</div>
                    <p className="mt-1 text-sm text-amber-800">
                      Buradaki <span className="font-semibold">zorunlu</span> olan bilgileri doldurmadığınız sürece yazıcı
                      çalışmayacaktır.
                    </p>
                    <div className="mt-3 rounded-xl bg-white/70 px-3 py-2 text-xs text-amber-900">
                      Zorunlu alanlar <span className="font-semibold">*</span> ile gösterilir.
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">Form Durumu</span>
                  <span className={isValid ? "font-semibold text-green-600" : "font-semibold text-red-600"}>
                    {isValid ? "Hazır" : "Eksik"}
                  </span>
                </div>

                <div className="mt-2 text-xs text-gray-600">
                  {isValid ? (
                    <span>Kaydetmeye hazır. Yazıcı çalışabilir.</span>
                  ) : (
                    <span>
                      Kalan zorunlu alan: <span className="font-semibold">{missingCount}</span>
                    </span>
                  )}
                </div>

                <div className="mt-3 text-xs text-gray-600">
                  Değişiklik:{" "}
                  <span className={isDirty ? "font-semibold text-amber-700" : "font-semibold text-gray-700"}>
                    {isDirty ? "Kaydedilmedi" : "Kaydedildi"}
                  </span>
                </div>

                {/* ✅ Backend iscompleted varsa yazıcı durumu */}
                <div className="mt-3 text-xs text-gray-600">
                  Yazıcı Durumu:{" "}
                  <span className={isCompleted ? "font-semibold text-green-700" : "font-semibold text-red-700"}>
                    {isCompleted ? "Aktif" : "Pasif"}
                  </span>
                </div>
              </div>

              <DepartmentsPanel
                departments={normalizeDepartmentsFromBackend(form.departments)}
                editorOpen={departmentEditorOpen}
                departmentName={departmentName}
                departmentNumber={departmentNumber}
                editingIndex={editingDepartmentIndex}
                saving={departmentSaving}
                error={departmentError}
                onAddClick={openNewDepartmentEditor}
                onNameChange={setDepartmentName}
                departmentVatRate={departmentVatRate}
                onNumberChange={setDepartmentNumber}
                onVatRateChange={setDepartmentVatRate}
                onSave={handleSaveDepartment}
                onCancel={resetDepartmentEditor}
                onEdit={openEditDepartmentEditor}
                onDelete={handleDeleteDepartment}
              />
            </div>
          </div>
        </div>

        {/* ✅ Kaydetmeden çıkış modalı */}
        <ConfirmLeaveModal
          open={leaveModalOpen}
          saving={saving}
          onClose={onCancelLeave}
          onLeave={onLeaveWithoutSaving}
          onSaveAndLeave={onSaveAndContinue}
        />
      </div>
    </div>
  );
};

/* ---------------- UI Components ---------------- */

const FullPageLoader = ({ title = "Yükleniyor...", subtitle = "" }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center px-4">
        <div className="w-full rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-gray-900" />
            <div>
              <div className="text-base font-semibold text-gray-900">{title}</div>
              {subtitle ? <div className="mt-1 text-sm text-gray-600">{subtitle}</div> : null}
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <div className="h-3 w-2/3 animate-pulse rounded bg-gray-100" />
            <div className="h-3 w-full animate-pulse rounded bg-gray-100" />
            <div className="h-3 w-5/6 animate-pulse rounded bg-gray-100" />
            <div className="h-3 w-3/4 animate-pulse rounded bg-gray-100" />
          </div>

          <div className="mt-6 text-xs text-gray-500">Not: Bilgiler gelmeden form ekrana gelmez.</div>
        </div>
      </div>
    </div>
  );
};

const Card = ({ title, children }) => {
  return (
    <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
};

const TwoColRow = ({ label, required = false, value, onChange, placeholder, error }) => {
  const hasError = Boolean(error);
  return (
    <div className="grid grid-cols-12 items-start gap-3">
      <div className="col-span-12 sm:col-span-4">
        <div className="text-sm font-medium text-gray-800">
          {label}
          {required && <span className="ml-1 text-red-600">*</span>}
        </div>
      </div>

      <div className="col-span-12 sm:col-span-8">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={[
            "w-full rounded-xl border px-3 py-2 text-sm outline-none transition",
            hasError ? "border-red-500 bg-red-50 focus:border-red-600" : "border-gray-300 bg-white focus:border-gray-900",
          ].join(" ")}
        />
        {hasError && <div className="mt-1 text-xs text-red-600">{error}</div>}
      </div>
    </div>
  );
};


const DepartmentsPanel = ({
  departments,
  editorOpen,
  departmentName,
  departmentNumber,
  departmentVatRate,
  editingIndex,
  saving,
  error,
  onAddClick,
  onNameChange,
  onNumberChange,
  onVatRateChange,
  onSave,
  onCancel,
  onEdit,
  onDelete,
}) => {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">Departmanlar</div>
          <div className="mt-1 text-xs text-gray-500">Format: departman ismi,departman numarası,KDV oranı</div>
        </div>

        <button
          onClick={onAddClick}
          disabled={saving}
          className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Departman Ekle
        </button>
      </div>

      {editorOpen && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <div className="text-xs font-semibold text-gray-800">
            {editingIndex !== null ? "Departman Düzenle" : "Yeni Departman"}
          </div>

          <div className="mt-3 space-y-2">
            <input
              value={departmentName}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Departman ismi / Örn: yiyecek"
              disabled={saving}
              className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
            />

            <input
              value={departmentNumber}
              onChange={(e) => onNumberChange(e.target.value)}
              placeholder="Departman numarası / Örn: 1"
              inputMode="numeric"
              disabled={saving}
              className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
            />

            <input
              value={departmentVatRate}
              onChange={(e) => onVatRateChange(e.target.value)}
              placeholder="KDV oranı / Örn: 20"
              inputMode="decimal"
              disabled={saving}
              className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          {error && <div className="mt-2 text-xs text-red-600">{error}</div>}

          <div className="mt-3 flex gap-2">
            <button
              onClick={onSave}
              disabled={saving}
              className="flex-1 rounded-xl bg-black px-3 py-2 text-xs font-medium text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Kaydediliyor..." : editingIndex !== null ? "Güncelle" : "Kaydet"}
            </button>

            <button
              onClick={onCancel}
              disabled={saving}
              className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              İptal
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2">
        {departments.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 px-3 py-3 text-xs text-gray-500">
            Henüz departman eklenmedi.
          </div>
        ) : (
          departments.map((department, index) => {
            const parsed = parseDepartmentText(department);

            return (
              <div key={`${department}-${index}`} className="flex items-center justify-between gap-2 rounded-xl border border-gray-200 px-3 py-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-gray-900">{parsed.name || "İsimsiz Departman"}</div>
                  <div className="mt-0.5 text-xs text-gray-500">
                    No: <span className="font-semibold text-gray-700">{parsed.number || "-"}</span>
                    <span className="ml-2">KDV: <span className="font-semibold text-gray-700">%{parsed.vatRate || "-"}</span></span>
                    <span className="ml-2 text-gray-400">DB: {department}</span>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <button
                    onClick={() => onEdit(index)}
                    disabled={saving}
                    className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                    title="Düzenle"
                    aria-label="Departmanı düzenle"
                  >
                    ✎
                  </button>

                  <button
                    onClick={() => onDelete(index)}
                    disabled={saving}
                    className="rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-sm text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                    title="Sil"
                    aria-label="Departmanı sil"
                  >
                    Sil
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const ConfirmLeaveModal = ({ open, saving, onClose, onLeave, onSaveAndLeave }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-gray-900">Kaydetmeden gitmek istiyor musunuz?</div>
            <p className="mt-1 text-sm text-gray-600">Değişiklikleriniz kaybolabilir. İstersen kaydedip devam edebilirsin.</p>
          </div>

          <button
            onClick={onClose}
            className="rounded-xl border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
            aria-label="Kapat"
          >
            ✕
          </button>
        </div>

        <div className="mt-5 flex flex-col gap-2">
          <button
            onClick={onSaveAndLeave}
            disabled={saving}
            className="rounded-xl bg-black px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? "Kaydediliyor..." : "Kaydet ve devam et"}
          </button>

          <button
            onClick={onLeave}
            disabled={saving}
            className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Kaydetmeden devam et
          </button>

          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Iptal
          </button>
        </div>

        <div className="mt-4 rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-600">
          Not: Backend eklenmiştir; <span className="font-semibold">Kaydet ve devam et</span> gerçek kaydı yapar.
        </div>
      </div>
    </div>
  );
};

export default ReceiptInfoPage;